import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import UnauthorizedModal from "../UnauthorizedModal";
import GitProviderAccessModal from "../GitProviderAccessModal";
import ProjectLaunchProgress from "./ProjectLaunchProgress";
import InstallTempoModal from "./InstallTempoModal";
import React from "react";
import { track } from "@/utils/events/tracker";
import { DeletedModal } from "../ProjectList/DeletedModal";
import ServerDownModal from "../ServerDownModal";
import { useTypedSelector } from "@/store/store";

interface Props {}

const CanvasLoadingOverlay: React.FC<Props> = React.memo(() => {
  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );

  const unauthorized = useSelector((state: any) => state.codebase.unauthorized);
  const deleted = useSelector((state: any) => state.codebase.deleted);
  const serverDown = useSelector((state: any) => state.codebase.serverDown);
  const githubAccess = useSelector((state: any) => state.codebase.githubAccess);
  const gitlabAccess = useTypedSelector((state) => state.codebase.gitlabAccess);
  const bitbucketAccess = useTypedSelector(
    (state) => state.codebase.bitbucketAccess
  );
  const showDevtoolModal = useCodebaseStore((state) => state.showDevtoolModal);

  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus
  );

  const [devServerNotRunningCount, setDevServerNotRunningCount] = useState(0);
  const [certainDevServerNotRunning, setCertainDevServerNotRunning] =
    useState(false);

  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress
  );

  const devServerResponsive =
    projectLoadingProgress?.dev_server_responding === true;

  const containerPaused =
    (devServerStatus?.containerStatus?.found ||
      projectLaunchStatus === "succeeded") &&
    !devServerStatus?.containerStatus?.running;

  const terminalDisconnected =
    projectLaunchStatus === "succeeded" &&
    devServerStatus?.containerStatus?.found &&
    devServerStatus?.containerStatus?.running &&
    !devServerStatus?.devServerConsoleConnected;

  const devServerNotRunning =
    projectLaunchStatus === "succeeded" &&
    devServerStatus?.containerStatus?.found &&
    devServerStatus?.containerStatus?.running &&
    !devServerStatus?.isServerRunning;

  /**
   * Track in posthog
   */
  useEffect(() => {
    if (terminalDisconnected) {
      track("TERMINAL_DISCONNECTED");
    }
  }, [terminalDisconnected]);

  useEffect(() => {
    if (devServerNotRunning) {
      setDevServerNotRunningCount((prev) => prev + 1);
      if (devServerNotRunningCount >= 2) {
        setCertainDevServerNotRunning(true);
      }
    } else {
      setDevServerNotRunningCount(0);
      setCertainDevServerNotRunning(false);
    }
  }, [devServerStatus, devServerNotRunning]);

  // Whether to show the progress modal at all
  let showGithubModal = false;
  let showGitlabModal = false;
  let showBitbucketModal = false;
  let showUnauthorizedModal = false;
  let showInstallTempoModal = false;
  let showProgressModal = false;
  let showDeletedModal = false;
  let showServerDownModal = false;
  if (unauthorized && !serverDown) {
    showUnauthorizedModal = true;
  } else if (deleted) {
    showDeletedModal = true;
  } else if (githubAccess === false) {
    showGithubModal = true;
  } else if (gitlabAccess === false) {
    showGitlabModal = true;
  } else if (bitbucketAccess === false) {
    showBitbucketModal = true;
  } else if (serverDown) {
    showServerDownModal = true;
  } else if (showDevtoolModal) {
    showInstallTempoModal = true;
  } else if (projectLaunchStatus !== "succeeded") {
    showProgressModal = true;
  } else if (
    containerPaused ||
    certainDevServerNotRunning ||
    !devServerResponsive
    // terminalDisconnected
  ) {
    showProgressModal = true;
  }

  useEffect(() => {
    if (showDeletedModal) {
      track("CANVAS_OVERLAY", {
        type: "CANVAS_DELETED",
      });
    }
  }, [showDeletedModal]);

  useEffect(() => {
    if (showUnauthorizedModal) {
      track("CANVAS_OVERLAY", {
        type: "UNAUTHORIZED",
      });
    }
  }, [showUnauthorizedModal]);

  useEffect(() => {
    if (showGithubModal) {
      track("CANVAS_OVERLAY", {
        type: "GITHUB_ACCESS",
      });
    }
  }, [showGithubModal]);

  useEffect(() => {
    if (showServerDownModal) {
      track("CANVAS_OVERLAY", {
        type: "SERVER_DOWN",
      });
    }
  }, [showServerDownModal]);

  useEffect(() => {
    if (showInstallTempoModal) {
      track("CANVAS_OVERLAY", {
        type: "INSTALL_TEMPO",
      });
    }
  }, [showInstallTempoModal]);

  return (
    <>
      {showServerDownModal && <ServerDownModal />}
      {showUnauthorizedModal && <UnauthorizedModal />}
      {showGithubModal && <GitProviderAccessModal provider="Github" />}
      {showGitlabModal && <GitProviderAccessModal provider="GitLab" />}
      {showBitbucketModal && <GitProviderAccessModal provider="Bitbucket" />}
      <ProjectLaunchProgress
        hidden={!showProgressModal}
        containerPaused={containerPaused}
        devServerNotRunning={certainDevServerNotRunning}
        terminalDisconnected={false}
      />
      {showInstallTempoModal && <InstallTempoModal />}
      {showDeletedModal && <DeletedModal />}
    </>
  );
});

export default CanvasLoadingOverlay;
